/**
 * Learning Hub page gutter — matches "Learning Hub" / "Flashcards" headers
 * (`px-5` = 20px mobile, `sm:px-8` = 32px desktop).
 *
 * Do not put this on the overflow flex scroller: WebKit/Chromium collapse
 * inline padding there, and `scroll-snap-type: x mandatory` then snaps the
 * first card to x=0. Apply the gutter on the inner track and pair it with
 * matching scroll-padding so snap cannot hide that inset.
 */
export const HUB_GUTTER_X = 'px-5 sm:px-8';

export const HUB_SCROLL_PAD_X = 'scroll-pl-5 scroll-pr-5 sm:scroll-pl-8 sm:scroll-pr-8';

/**
 * Overflow row only — no horizontal padding (that lives on the inner track).
 *
 * `touch-pan-y` (not `touch-pan-x`): a horizontal scroller with `pan-x` eats
 * vertical page scroll on phones. The page owns vertical pans; `HubCarouselScroller`
 * takes over only after a swipe is clearly sideways, then coasts with inertia
 * so the row still feels like native overflow-x instead of a dead drag.
 */
export const HUB_CAROUSEL_SCROLLER = `min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain ${HUB_SCROLL_PAD_X} touch-pan-y [container-type:inline-size] [-ms-overflow-style:none] [scrollbar-width:none] [scroll-snap-type:x_mandatory] [&::-webkit-scrollbar]:hidden`;

export function decideCarouselAxis(dx: number, dy: number, threshold = 8): 'x' | 'y' | null {
  if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return null;
  return Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
}

export const CAROUSEL_INERTIA_FRICTION = 0.96;
export const CAROUSEL_INERTIA_MIN_SPEED = 0.05;
export const CAROUSEL_INERTIA_MAX_SPEED = 3;
export const CAROUSEL_VELOCITY_WINDOW_MS = 80;

export type CarouselTouchSample = { t: number; x: number };

/** Finger velocity in px/ms from the tail of a swipe (positive = finger right). */
export function carouselFingerVelocity(
  samples: readonly CarouselTouchSample[],
  now: number,
  windowMs = CAROUSEL_VELOCITY_WINDOW_MS,
): number {
  const recent = samples.filter((sample) => now - sample.t <= windowMs && now >= sample.t);
  if (recent.length < 2) return 0;
  const first = recent[0];
  const last = recent[recent.length - 1];
  const dt = last.t - first.t;
  if (dt < 12) return 0;
  const velocity = (last.x - first.x) / dt;
  if (!Number.isFinite(velocity)) return 0;
  return Math.max(-CAROUSEL_INERTIA_MAX_SPEED, Math.min(CAROUSEL_INERTIA_MAX_SPEED, velocity));
}

/**
 * One coast frame after release. Finger-right decreases `scrollLeft`.
 * Snap stays off until `done` so mandatory snap cannot fight the flick.
 */
export function carouselInertiaFrame(
  scrollLeft: number,
  fingerVelocity: number,
  dtMs: number,
  maxScroll: number,
  friction = CAROUSEL_INERTIA_FRICTION,
): { scrollLeft: number; velocity: number; done: boolean } {
  const dt = Math.max(0, dtMs);
  const max = Math.max(0, maxScroll);
  const nextLeft = Math.min(max, Math.max(0, scrollLeft - fingerVelocity * dt));
  const nextVelocity = fingerVelocity * friction ** (dt / 16);
  const atEnd = nextLeft <= 0 || nextLeft >= max;
  const done = atEnd || Math.abs(nextVelocity) < CAROUSEL_INERTIA_MIN_SPEED;
  return { scrollLeft: nextLeft, velocity: done ? 0 : nextVelocity, done };
}

/** Padded flex track inside the scroller. `w-max` keeps start/end gutters in the scroll width. */
export const HUB_CAROUSEL_TRACK = `flex w-max min-w-full flex-nowrap ${HUB_GUTTER_X} pb-1`;

/** Featured card: fraction of the scroller, not `100vw` (viewport ignores gutter + sidebar). */
export const HERO_CARD_WIDTH = 'w-[calc(100cqw-4.5rem)] sm:w-[min(36rem,calc(100cqw-2rem))]';

/** Matches mobile `min(windowWidth / 2 - 24, 250)` so two cards sit with a peek. */
export const RESOURCE_PEEK_WIDTH = 'w-[min(calc(50cqw-1.5rem),15.625rem)]';
